import type { ApprovalDetailsRow, ApprovalDetailsSection, DocumentFile, DocumentGroup } from '../types';
import ApprovalDetailsDocumentList from './ApprovalDetailsDocumentList';
import ApprovalDetailsEditableRow, { type ApprovalEditState } from './ApprovalDetailsEditableRow';

interface ApprovalDetailsSectionContentProps {
  applicationId?: string;
  editStateByRowId: Record<string, ApprovalEditState>;
  section: ApprovalDetailsSection;
  onCancel: (rowId: string) => void;
  onEdit: (rowId: string) => void;
  onReasonChange: (rowId: string, value: string) => void;
  onRemove: (rowId: string) => void;
  onRequestEdit: (rowId: string) => void;
  onSave: (rowId: string) => void;
}

function renderFieldSummary(row: ApprovalDetailsRow) {
  return (
    <dl className='approval-details-field-summary'>
      <dt className='approval-details-field-summary__label'>{row.label}</dt>
      <dd className='approval-details-field-summary__value'>{row.value}</dd>
    </dl>
  );
}

function renderDocumentSummary(file: DocumentFile, applicationId?: string) {
  return (
    <ApprovalDetailsDocumentList
      applicationId={applicationId}
      files={[file]}
    />
  );
}

function renderDocumentGroup(
  group: DocumentGroup,
  applicationId: string | undefined,
  props: Omit<ApprovalDetailsSectionContentProps, 'applicationId' | 'section'>,
  sectionEditable: boolean,
  isAlternate: boolean,
) {
  const { editStateByRowId, onCancel, onEdit, onReasonChange, onRemove, onRequestEdit, onSave } = props;

  return (
    <div
      key={group.id}
      className={`approval-details-document-group${isAlternate ? ' approval-details-document-group--alternate' : ''}`}
      data-testid={`approval-details-document-group-${group.id}`}>
      <p className='approval-details-document-summary__title'>{group.title}</p>
      {group.documents.map((file) => (
        <ApprovalDetailsEditableRow
          key={file.id}
          isDocumentRow
          editState={editStateByRowId[file.id]}
          id={file.id}
          isEditable={sectionEditable}
          summary={renderDocumentSummary(file, applicationId)}
          onCancel={onCancel}
          onEdit={onEdit}
          onReasonChange={onReasonChange}
          onRemove={onRemove}
          onRequestEdit={onRequestEdit}
          onSave={onSave}
        />
      ))}
    </div>
  );
}

function ApprovalDetailsSectionContent({
  applicationId,
  editStateByRowId,
  section,
  onCancel,
  onEdit,
  onReasonChange,
  onRemove,
  onRequestEdit,
  onSave,
}: ApprovalDetailsSectionContentProps) {
  const baseTestId = `approval-details-section-content-${section.id}`;

  if (section.documentGroups) {
    return (
      <div
        className='approval-details-section-content'
        data-testid={baseTestId}>
        {section.documentGroups.map((group, index) =>
          renderDocumentGroup(
            group,
            applicationId,
            { editStateByRowId, onCancel, onEdit, onReasonChange, onRemove, onRequestEdit, onSave },
            section.editable !== false,
            index % 2 === 1,
          ),
        )}
      </div>
    );
  }

  return (
    <div
      className='approval-details-section-content'
      data-testid={baseTestId}>
      {section.rows.map((row) => (
        <ApprovalDetailsEditableRow
          key={row.id}
          editState={editStateByRowId[row.id]}
          id={row.id}
          isEditable={row.isEditable ?? section.editable !== false}
          summary={renderFieldSummary(row)}
          onCancel={onCancel}
          onEdit={onEdit}
          onReasonChange={onReasonChange}
          onRemove={onRemove}
          onRequestEdit={onRequestEdit}
          onSave={onSave}
        />
      ))}
    </div>
  );
}

export default ApprovalDetailsSectionContent;
